import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, Mail, Wand2 } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { emailPayslipNow } from "@/lib/payroll.functions";
import { toast } from "sonner";
import { formatRand } from "@/lib/invoice-details";
import {
  totals, suggest, monthBounds, formatPayPeriod, EARNING_LABELS, DEDUCTION_LABELS, EMPLOYER_LABELS,
  type Earnings, type Deductions, type Employer,
  DEFAULT_MONTHLY_HOURS,
} from "@/lib/payroll";
import { downloadPayslipPdf } from "@/lib/payslip-pdf";

export const Route = createFileRoute("/_authenticated/admin/payroll")({
  head: () => ({ meta: [{ title: "Payroll — N S Physiotherapy Admin" }, { name: "robots", content: "noindex" }] }),
  component: PayrollPage,
});

const thisMonth = () => new Date().toISOString().slice(0, 7);
const zeroE: Earnings = { basic: 0, commission: 0, overtime: 0, bonus: 0, allowances: 0, other: 0, benefits: 0 };
const zeroD: Deductions = { paye: 0, uif: 0, pension: 0, medical: 0, other: 0 };
const zeroR: Employer = { uif: 0, sdl: 0, pension: 0, other: 0 };

type Emp = {
  id: string; full_name: string; employee_number: string | null; job_title: string | null; email: string | null;
  id_number: string | null; tax_number: string | null; address: string | null; start_date: string | null;
  end_date: string | null; active: boolean; basic_salary: number; uif_applicable: boolean;
};

function PayrollPage() {
  const [month, setMonth] = useState(thisMonth());
  const qc = useQueryClient();
  const emps = useQuery({
    queryKey: ["payroll", "employees"],
    queryFn: async () => {
      const { data, error } = await supabase.from("payroll_employees").select("*").order("full_name");
      if (error) throw error;
      return data as Emp[];
    },
  });
  const slips = useQuery({
    queryKey: ["payroll", "slips"],
    queryFn: async () => {
      const { data, error } = await supabase.from("payslips").select("*").order("period", { ascending: false }).limit(200);
      if (error) throw error;
      return data as any[];
    },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["payroll"] });
  const monthSlips = (slips.data ?? []).filter((s) => s.period === month);
  const sum = (f: (s: any) => number) => monthSlips.reduce((a, s) => a + Number(f(s) || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label>Month</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} /></div>
        <p className="text-sm text-muted-foreground">All amounts in South African rand (ZAR).</p>
      </div>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        {[
          ["Active employees", String((emps.data ?? []).filter((e) => e.active).length)],
          ["Gross salaries", formatRand(sum((s) => s.gross))],
          ["Employee deductions", formatRand(sum((s) => s.total_deductions))],
          ["Net payable", formatRand(sum((s) => s.net))],
          ["PAYE", formatRand(sum((s) => s.deductions.paye))],
          ["Employee UIF", formatRand(sum((s) => s.deductions.uif))],
          ["Employer UIF", formatRand(sum((s) => s.employer.uif))],
          ["SDL", formatRand(sum((s) => s.employer.sdl))],
        ].map(([l, v]) => (
          <Card key={l}><CardContent className="p-4"><p className="text-xs text-muted-foreground">{l}</p><p className="text-lg font-semibold">{v}</p></CardContent></Card>
        ))}
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">SARS EMP201 summary — {month}</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-1">
          <p>PAYE: <b>{formatRand(sum((s) => s.deductions.paye))}</b></p>
          <p>UIF (employee + employer): <b>{formatRand(sum((s) => s.deductions.uif + s.employer.uif))}</b></p>
          <p>SDL: <b>{formatRand(sum((s) => s.employer.sdl))}</b></p>
          <p className="font-semibold">Total due to SARS: {formatRand(sum((s) => s.deductions.paye + s.deductions.uif + s.employer.uif + s.employer.sdl))}</p>
          <p className="text-xs text-muted-foreground">Check these figures against eFiling before submitting.</p>
        </CardContent>
      </Card>
      <Tabs defaultValue="run">
        <TabsList><TabsTrigger value="run">Create payslip</TabsTrigger><TabsTrigger value="slips">Payslips</TabsTrigger><TabsTrigger value="emps">Employees</TabsTrigger></TabsList>
        <TabsContent value="run"><PayRun emps={(emps.data ?? []).filter((e) => e.active)} month={month} onSaved={refresh} /></TabsContent>
        <TabsContent value="slips"><SlipList slips={slips.data ?? []} onChange={refresh} /></TabsContent>
        <TabsContent value="emps"><Employees emps={emps.data ?? []} onChange={refresh} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Money({ label, value, onChange }: { label: string; value: number; onChange: (n: number) => void }) {
  return (
    <div><Label className="text-xs">{label}</Label>
      <Input type="number" min={0} step="0.01" value={value || ""} onChange={(e) => onChange(Number(e.target.value) || 0)} />
    </div>
  );
}

function PayRun({ emps, month, onSaved }: { emps: Emp[]; month: string; onSaved: () => void }) {
  const [empId, setEmpId] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [pStart, setPStart] = useState(monthBounds(month).start);
  const [pEnd, setPEnd] = useState(monthBounds(month).end);
  useEffect(() => { const b = monthBounds(month); setPStart(b.start); setPEnd(b.end); }, [month]);
  const [sdl, setSdl] = useState(false);
  const [e, setE] = useState<Earnings>(zeroE);
  const [d, setD] = useState<Deductions>(zeroD);
  const [r, setR] = useState<Employer>(zeroR);
  const [notes, setNotes] = useState("");
  const [hrs, setHrs] = useState({ ordinary: DEFAULT_MONTHLY_HOURS, overtime: 0, sunday: 0, publicHoliday: 0 });
  const emp = emps.find((x) => x.id === empId);
  const t = useMemo(() => totals(e, d), [e, d]);

  function pick(id: string) {
    setEmpId(id);
    const x = emps.find((y) => y.id === id);
    setE({ ...zeroE, basic: Number(x?.basic_salary ?? 0) });
    setD(zeroD); setR(zeroR);
  }
  function auto() {
    const s = suggest(e, { uifApplicable: emp?.uif_applicable ?? true, sdlApplicable: sdl });
    setD({ ...d, paye: s.paye, uif: s.employeeUif });
    setR({ ...r, uif: s.employerUif, sdl: s.sdl });
  }
  async function save() {
    if (!emp) return toast.error("Select an employee");
    if (!pStart || !pEnd || pEnd < pStart) return toast.error("The period end date must be on or after the start date.");
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase.from("payslips").insert({
      employee_id: emp.id, period: pStart.slice(0, 7), period_start: pStart, period_end: pEnd, payment_date: payDate,
      employee_snapshot: { full_name: emp.full_name, employee_number: emp.employee_number, job_title: emp.job_title, tax_number: emp.tax_number, id_number: emp.id_number, address: emp.address, start_date: emp.start_date, basic_salary: String(emp.basic_salary ?? "") },
      earnings: e, deductions: d, employer: r, gross: t.gross, total_deductions: t.totalDeductions, net: t.net,
      notes: notes || null, created_by: u.user?.id, hours: hrs,
    });
    if (error) return toast.error(error.code === "23505" ? "A payslip for this employee and month already exists." : error.message);
    toast.success("Payslip saved as draft");
    onSaved(); setEmpId(""); setE(zeroE); setD(zeroD); setR(zeroR); setNotes("");
  }

  return (
    <Card><CardContent className="p-4 space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <div><Label>Employee</Label>
          <select className="w-full h-9 rounded-md border bg-background px-2 text-sm" value={empId} onChange={(ev) => pick(ev.target.value)}>
            <option value="">Select…</option>
            {emps.map((x) => <option key={x.id} value={x.id}>{x.full_name}</option>)}
          </select>
        </div>
        <div><Label>Pay period</Label>
          <div className="flex items-center gap-1">
            <Input type="date" value={pStart} onChange={(ev) => setPStart(ev.target.value)} aria-label="Period start" />
            <span>-</span>
            <Input type="date" value={pEnd} onChange={(ev) => setPEnd(ev.target.value)} aria-label="Period end" />
          </div>
        </div>
        <div><Label>Payment date</Label><Input type="date" value={payDate} onChange={(ev) => setPayDate(ev.target.value)} /></div>
      </div>
      <h3 className="font-semibold text-sm">Earnings</h3>
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
        {(Object.keys(EARNING_LABELS) as (keyof Earnings)[]).map((k) => <Money key={k} label={EARNING_LABELS[k]} value={e[k]} onChange={(n) => setE({ ...e, [k]: n })} />)}
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={sdl} onCheckedChange={(v) => setSdl(!!v)} /> SDL applies (payroll over R500,000/year)</label>
        <Button type="button" variant="outline" size="sm" onClick={auto}><Wand2 className="h-4 w-4 mr-1" />Calculate PAYE, UIF & SDL</Button>
      </div>
      <h3 className="font-semibold text-sm">Employee deductions</h3>
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-5">
        {(Object.keys(DEDUCTION_LABELS) as (keyof Deductions)[]).map((k) => <Money key={k} label={DEDUCTION_LABELS[k]} value={d[k]} onChange={(n) => setD({ ...d, [k]: n })} />)}
      </div>
      <h3 className="font-semibold text-sm">Employer contributions (not deducted from employee)</h3>
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
        {(Object.keys(EMPLOYER_LABELS) as (keyof Employer)[]).map((k) => <Money key={k} label={EMPLOYER_LABELS[k]} value={r[k]} onChange={(n) => setR({ ...r, [k]: n })} />)}
      </div>
      <h3 className="font-semibold text-sm">Hours worked this period</h3>
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
        <Money label="Ordinary hours" value={hrs.ordinary} onChange={(n) => setHrs({ ...hrs, ordinary: n })} />
        <Money label="Overtime hours" value={hrs.overtime} onChange={(n) => setHrs({ ...hrs, overtime: n })} />
        <Money label="Sunday hours" value={hrs.sunday} onChange={(n) => setHrs({ ...hrs, sunday: n })} />
        <Money label="Public holiday hours" value={hrs.publicHoliday} onChange={(n) => setHrs({ ...hrs, publicHoliday: n })} />
      </div>
      <div><Label>Adjustment notes (optional)</Label><Input value={notes} onChange={(ev) => setNotes(ev.target.value)} /></div>
      <div className="rounded-md border p-3 text-sm grid grid-cols-3 gap-2">
        <div>Gross<br /><b>{formatRand(t.gross)}</b></div>
        <div>Deductions<br /><b>{formatRand(t.totalDeductions)}</b></div>
        <div>Net payable<br /><b>{formatRand(t.net)}</b></div>
      </div>
      <p className="text-xs text-muted-foreground">PAYE uses the SARS 2026/27 tables for regular monthly pay. Always double-check bonuses and once-off payments.</p>
      <Button onClick={save} disabled={!emp}>Save payslip</Button>
    </CardContent></Card>
  );
}

function SlipList({ slips, onChange }: { slips: any[]; onChange: () => void }) {
  const sendFn = useServerFn(emailPayslipNow);
  const [sending, setSending] = useState<string | null>(null);
  async function email(id: string) {
    setSending(id);
    try {
      const r = await sendFn({ data: { id } });
      if (r.sent) toast.success(`Payslip emailed to ${r.to}. ${r.copies ? "Your copy was sent too." : "Your copy could not be sent."}`);
      else if (r.reason === "no_employee_email") toast.warning(`This employee has no email address. ${r.copies ? "Your copy was sent." : ""}`);
      else toast.error(`The payslip could not be emailed (${r.reason ?? "unknown"}).`);
      onChange();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not email payslip"); }
    finally { setSending(null); }
  }
  async function approve(id: string) {
    const { error } = await supabase.from("payslips").update({ status: "approved" }).eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Payslip approved"); onChange();
  }
  async function remove(id: string) {
    if (!confirm("Delete this draft payslip?")) return;
    const { error } = await supabase.from("payslips").delete().eq("id", id).eq("status", "draft");
    if (error) return toast.error(error.message);
    onChange();
  }
  if (!slips.length) return <p className="text-sm text-muted-foreground p-4">No payslips yet.</p>;
  return (
    <Card><CardContent className="p-0 divide-y">
      {slips.map((s) => (
        <div key={s.id} className="flex flex-wrap items-center gap-3 p-3 text-sm">
          <div className="flex-1 min-w-40"><b>{s.employee_snapshot?.full_name}</b> · {formatPayPeriod(s)}<br />
            <span className="text-muted-foreground">Net {formatRand(s.net)} · {s.status === "approved" ? "Approved" : "Draft — needs review"}</span></div>
          <Button size="sm" variant="outline" onClick={() => downloadPayslipPdf(s)}><Download className="h-4 w-4 mr-1" />PDF</Button>
          {s.status === "approved" && <Button size="sm" variant="outline" disabled={sending === s.id} onClick={() => email(s.id)}><Mail className="h-4 w-4 mr-1" />{sending === s.id ? "Sending…" : s.emailed_at ? "Email again" : "Email employee"}</Button>}
          {s.status === "draft" && <><Button size="sm" onClick={() => approve(s.id)}>Approve</Button><Button size="sm" variant="ghost" onClick={() => remove(s.id)}>Delete</Button></>}
        </div>
      ))}
    </CardContent></Card>
  );
}

const blank = { full_name: "", employee_number: "", job_title: "", email: "", id_number: "", tax_number: "", address: "", start_date: "", end_date: "", basic_salary: 0, uif_applicable: true, active: true };

function Employees({ emps, onChange }: { emps: Emp[]; onChange: () => void }) {
  const [f, setF] = useState<any>(blank);
  const [editId, setEditId] = useState<string | null>(null);
  async function save() {
    if (!f.full_name.trim()) return toast.error("Name is required");
    const row = { ...f, start_date: f.start_date || null, end_date: f.end_date || null, basic_salary: Number(f.basic_salary) || 0 };
    const { error } = editId
      ? await supabase.from("payroll_employees").update(row).eq("id", editId)
      : await supabase.from("payroll_employees").insert(row);
    if (error) return toast.error(error.message);
    toast.success("Employee saved"); setF(blank); setEditId(null); onChange();
  }
  const txt = (k: string, l: string, type = "text") => (
    <div><Label className="text-xs">{l}</Label><Input type={type} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></div>
  );
  return (
    <div className="space-y-4">
      <Card><CardContent className="p-4 space-y-3">
        <h3 className="font-semibold text-sm">{editId ? "Edit employee" : "Add employee"}</h3>
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
          {txt("full_name", "Full name & surname")}{txt("employee_number", "Employee number")}{txt("job_title", "Job title")}
          {txt("email", "Email", "email")}{txt("id_number", "ID / passport number")}{txt("tax_number", "Tax reference number")}
          {txt("address", "Residential address")}{txt("start_date", "Start date", "date")}{txt("end_date", "End date", "date")}
          {txt("basic_salary", "Basic monthly salary (R)", "number")}
        </div>
        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-2"><Checkbox checked={f.uif_applicable} onCheckedChange={(v) => setF({ ...f, uif_applicable: !!v })} />UIF applies</label>
          <label className="flex items-center gap-2"><Checkbox checked={f.active} onCheckedChange={(v) => setF({ ...f, active: !!v })} />Active</label>
        </div>
        <p className="text-xs text-muted-foreground">Changing a salary here doesn't change payslips already saved.</p>
        <div className="flex gap-2"><Button onClick={save}>Save employee</Button>{editId && <Button variant="ghost" onClick={() => { setEditId(null); setF(blank); }}>Cancel</Button>}</div>
      </CardContent></Card>
      <Card><CardContent className="p-0 divide-y">
        {emps.length === 0 && <p className="p-4 text-sm text-muted-foreground">No employees yet.</p>}
        {emps.map((x) => (
          <div key={x.id} className="flex items-center gap-3 p-3 text-sm">
            <div className="flex-1"><b>{x.full_name}</b> {!x.active && <span className="text-muted-foreground">(inactive)</span>}<br />
              <span className="text-muted-foreground">{x.job_title ?? "—"} · {formatRand(x.basic_salary)}/month</span></div>
            <Button size="sm" variant="outline" onClick={() => { setEditId(x.id); setF({ ...blank, ...Object.fromEntries(Object.entries(x).map(([k, v]) => [k, v ?? ""])) }); window.scrollTo({ top: 0, behavior: "smooth" }); }}>Edit</Button>
          </div>
        ))}
      </CardContent></Card>
    </div>
  );
}
